import React, { useState } from 'react';
import {
  Box,
  Fab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
  IconButton,
  Card,
  CardContent,
} from '@mui/material';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import CloseIcon from '@mui/icons-material/Close';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import { styled } from '@mui/material/styles';

const FloatingButton = styled(Fab)(({ theme }) => ({
  position: 'fixed',
  bottom: theme.spacing(3),
  right: theme.spacing(3),
  backgroundColor: '#25D366',
  color: 'white',
  '&:hover': {
    backgroundColor: '#128C7E',
    transform: 'scale(1.1)',
  },
  transition: 'all 0.3s ease',
  zIndex: 1000,
}));

const WhatsAppWidget = () => {
  const [open, setOpen] = useState(false);

  // ✅ Correct WhatsApp number (NO + sign, NO dashes)
  const whatsappNumber = '+27763814178';

  const departments = [
    {
      name: 'General Support',
      number: whatsappNumber,
      icon: <SupportAgentIcon />,
      description: 'For general inquiries and support',
    },
    {
      name: 'Banking Services',
      number: whatsappNumber,
      icon: <AccountBalanceIcon />,
      description: 'Account opening, transactions, etc.',
    },
    {
      name: 'Credit Cards',
      number: whatsappNumber,
      icon: <CreditCardIcon />,
      description: 'Card applications and queries',
    },
    {
      name: 'Loan Department',
      number: whatsappNumber,
      icon: <MonetizationOnIcon />,
      description: 'Loan applications and information',
    },
  ];

  const handleWhatsAppClick = (number, department) => {
    const message = `Hello ${department} team! I need assistance with DiamondBank.`;
    const whatsappUrl = `https://wa.me/${number}?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');
    setOpen(false);
  };

  return (
    <>
      <FloatingButton
        onClick={() => setOpen(true)}
        aria-label="whatsapp-support"
      >
        <WhatsAppIcon fontSize="large" />
      </FloatingButton>

      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Typography
            variant="h6"
            sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
          >
            <WhatsAppIcon sx={{ color: '#25D366' }} />
            WhatsApp Support
          </Typography>
          <IconButton onClick={() => setOpen(false)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mb: 3 }}
          >
            Select a department to start a WhatsApp conversation:
          </Typography>

          <List>
            {departments.map((dept, index) => (
              <ListItem
                key={`${dept.name}-${index}`}
                button
                onClick={() => handleWhatsAppClick(dept.number, dept.name)}
                sx={{
                  mb: 1,
                  borderRadius: 2,
                  '&:hover': {
                    backgroundColor: 'rgba(37, 211, 102, 0.1)',
                  },
                }}
              >
                <ListItemIcon sx={{ color: '#25D366' }}>
                  {dept.icon}
                </ListItemIcon>
                <Box sx={{ flex: 1 }}>
                  <ListItemText
                    primary={dept.name}
                    secondary={dept.description}
                  />
                </Box>
                <WhatsAppIcon sx={{ color: '#25D366' }} />
              </ListItem>
            ))}
          </List>

          <Card sx={{ mt: 3, bgcolor: 'rgba(37, 211, 102, 0.05)' }}>
            <CardContent>
              <Typography
                variant="body2"
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  mb: 1,
                }}
              >
                <WhatsAppIcon
                  fontSize="small"
                  sx={{ color: '#25D366' }}
                />
                <strong>Business Hours:</strong>
              </Typography>
              <Typography variant="body2">
                Monday - Friday: 9:00 AM - 6:00 PM
              </Typography>
              <Typography variant="body2">
                Saturday: 10:00 AM - 2:00 PM
              </Typography>
            </CardContent>
          </Card>
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default WhatsAppWidget;